<template>
    <page>
        <q-resize-observable @resize="resetAvailableCardHeight"/>
        <div class="shadow-1 self-stretch full-width" v-if="options" ref="container">
            <v-header :title="$metaInfo.title" :icon="options.icon" ref="header">
                <div slot="right" class="row no-wrap">
                    <v-button v-if="1/* check if the create route exists */"
                              flat round dense color="dark"
                              icon="add circle outline"
                              @click="createRow">
                        <q-tooltip anchor="top left" self="bottom middle">
                            {{ $T('add new') }}
                        </q-tooltip>
                    </v-button>
                    <v-button v-show="!isActiveToolbar('filter')"
                              flat round dense no-caps color="dark"
                              icon="search"
                              @click="toggleToolbar('filter')">
                        <q-tooltip v-show="!isActiveToolbar('filter')"
                                   anchor="top left" self="bottom middle">
                            {{ $T('search') }}
                        </q-tooltip>
                    </v-button>
                    <q-input v-if="isActiveToolbar('filter')"
                             style="width:260px"
                             hide-underline autofocus color="dark"
                             v-model="tempFilter"
                             :placeholder="$T('enter a search criteria...')"
                             @input="updateFilter"
                             class="original"
                             :before="[{icon: 'search', handler () { toggleToolbar('filter') }}]"/>
                    <v-button v-show="!isActiveToolbar('columns') && columnsOptions.length > 0"
                              flat round dense no-caps color="dark"
                              icon="view column"
                              @click="toggleToolbar('columns')">
                        <q-tooltip v-show="!isActiveToolbar('columns')"
                                   anchor="top left" self="bottom middle">
                            {{ $T('columns') }}
                        </q-tooltip>
                    </v-button>
                    <select-extended v-show="isActiveToolbar('columns')"
                                     select-all multiple toggle hide-underline
                                     class="original"
                                     color="dark"
                                     v-model="visibleColumns"
                                     :options="columnsOptions"
                                     :display-value="$T('columns')"
                                     :before="[{icon: 'view column', handler () { toggleToolbar('columns') }}]"/>
                </div>
            </v-header>
            <v-table ref="table"
                     :columnDefs="options.columns"
                     :resource-path="options.resourcePath"
                     :filter="filter"
                     :visible-columns="visibleColumns"
                     :context="context"
                     :height="availableCardHeight"
                     @ready="getAvailableCardHeight">
            </v-table>
        </div>
        <spinner-iqos size="60px" v-else/>
    </page>
</template>

<script>
    import {
        debounce,
        QInput,
        QPopover,
        QTooltip,
        QResizeObservable
    } from 'quasar-framework'
    import axios from 'axios'
    import {userPreferences} from '../store/modules/userPreferences'
    import ListButtons from './ListButtons'

    export default {
        name: 'list',

        metaInfo() {
            return {
                title: (this.isLoading ? this.$T('loading') + ' ' : '') + (this.options ? this.$T(this.options.plural) +
                    (this.options.isTrash ? ' ' + this.$t('in trash') : '') : '...')
            }
        },

        components: {
            QInput,
            QPopover,
            QTooltip,
            ListButtons,
            QResizeObservable
        },

        props: {
            slug: {
                type: String,
                required: true
            },
            baseRoute: {
                type: String,
                required: true
            },
            color: {
                type: String,
                default: 'white'
            }
        },

        methods: {
            updateFilter: debounce(function () {
                this.filter = this.tempFilter
                this.$nextTick(() => this.$refs.table.refresh())
            }, 500),

            notify(message, detail, actions = [], type = 'positive') {
                this.$q.notify({
                    type,
                    position: 'top',
                    message,
                    detail,
                    timeout: 5000,
                    actions
                })
            },

            createRow() {
                this.$router.push({name: `${this.baseRoute}.create`})
            },

            editRow(id) {
                this.$router.push({name: `${this.baseRoute}.edit`, params: {id}})
            },

            deleteRow(id) {
                this.request('delete', this.itemPath(id))
                    .then(data => {
                        this.notify(this.$T('success'), data.success.message, [{
                            label: this.$t('undo'), handler: function () {
                                this.restoreRow(data.model.id);
                            }.bind(this)
                        }])
                        this.$refs.table.refresh()
                    })
            },

            deleteRowForever(id) {
                this.request('delete', `${this.itemPath(id)}\/force`)
                    .then(data => {
                        this.notify(this.$T('success'), data.success.message)
                        this.$refs.table.refresh()
                    })
            },

            restoreRow(id) {
                this.request('get', `${this.itemPath(id)}\/restore`)
                    .then(data => {
                        this.notify(this.$T('success'), data.success.message)
                        this.$refs.table.refresh()
                    })
            },

            itemPath(id) {
                return `${this.options.resourcePath}\/${id}`
            },

            isActiveToolbar(item) {
                return this.activeToolbar == item
            },

            toggleToolbar(item) {
                this.activeToolbar = this.activeToolbar == item ? null : item
            },

            /**
             * Submit a request.
             *
             * @param {string} requestType
             * @param {string} url
             */
            request(requestType, url, data = {}) {
                this.isLoading = true

                return new Promise((resolve, reject) => {
                    axios[requestType](url, data)
                        .then(response => {
                            this.onSuccess(response.data);

                            resolve(response.data);
                        })
                        .catch(error => {
                            this.onFail(error.response.data);

                            reject(error.response.data);
                        })
                });
            },

            /**
             * Handle a successful request.
             *
             * @param {object} data
             */
            onSuccess(data) {
                this.isLoading = false
            },

            /**
             * Handle a failed request.
             *
             * @param {object} errors
             */
            onFail(data) {
                this.isLoading = false

                if (data && data.error)
                    this.notify(this.$T('error'), data.error.message, [], 'negative')
            },

            async describe() {
                await this.$store.dispatch('crud/fetchList', this.slug)
            },

            async init() {
                await this.describe()

                let options = deepCopy(this.$store.getters['crud/listDescription'](this.slug))
                if (!options) return

                // Localize labels and set default values for columns
                if (options.columns) {
                    let columns = options.columns
                    // For some reason PHP casts some arrays as objects. Convert it back to array here:
                    if (typeof columns === "object")
                        columns = Object.keys(columns).map(key => (columns[key]))
                    options.columns = columns.map((column, i) => ({
                        colId: column.field,
                        headerName: this.$T(column.label),
                        field: column.field,
                        headerClass: `text-${column.align || 'left'}`,
                        cellClass: `text-${column.align || 'left'}`,
                        suppressSorting: !(column.sortable || false),
                        lockVisible: column.required || false,
                        autoHeight: true,
                        hide: !(column.required || this.visibleColumns.includes(column.field)),
                        cellRenderer: i > 0 ? null : (params) => {
                            if (params.value === undefined)
                                return this.$T('loading') + '...'
                            return params.value
                        }
                    }))
                }

                if (options.columns && options.columns[options.columns.length - 1].colId != 'actions')
                    options.columns.push({
                        colId: 'actions',
                        field: 'id',
                        headerName: '',
                        cellClass: 'text-center',
                        suppressSorting: true,
                        suppressFilter: true,
                        suppressMenu: true,
                        suppressMovable: true,
                        suppressResize: true,
                        lockVisible: true,
                        pinned: 'right',
                        width: 80,
                        maxWidth: 80,
                        minWidth: 80,
                        cellStyle: {padding: 0},
                        cellRendererFramework: ListButtons
                    })

                this.options = Object.assign(deepCopy(this.defaultOptions), options)
            },

            async reload() {
                await this.init()
            },

            getAvailableCardHeight() {
                if (!this.$refs.container) return
                if (!this.$refs.header) return

                let containerEl = this.$refs.container
                let headerEl = this.$refs.header.$el
                this.availableCardHeight = containerEl.offsetHeight - headerEl.offsetHeight + 'px'
            },

            resetAvailableCardHeight() {
                this.availableCardHeight = '300px'

                this.$nextTick(() => this.getAvailableCardHeight())
            }
        },

        mounted() {
            this.init()
        },

        watch: {
            slug: 'reload'
        },

        computed: {
            columnsOptions() {
                return this.options.columns.filter(c => !c.lockVisible).map(c => ({
                    value: c.field,
                    label: c.headerName
                }))
            },
            visibleColumns: userPreferences('visibleColumns', []),
            context() {
                return {
                    methods: {
                        editRow: this.editRow,
                        deleteRow: this.deleteRow,
                        deleteRowForever: this.deleteRowForever,
                        restoreRow: this.restoreRow
                    },
                    messages: {
                        'are you sure?': this.$T('are you sure?'),
                        'delete': this.$T('delete'),
                        'cancel': this.$T('cancel'),
                        'delete forever': this.$T('delete forever')
                    }
                }
            }
        },

        data() {
            return {
                activeToolbar: this.$route.query.search ? 'filter' : '',
                defaultOptions: {
                    plural: 'items',
                    isTrash: false,
                    singular: 'item'
                },
                isLoading: false,
                filter: '',
                tempFilter: '',
                options: null,
                availableCardHeight: '300px'
            }
        }
    }
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/List__.vue